import type { FC, ReactNode } from 'react'

import Image from 'next/image'
import { useI18n } from '@/i18n'
import { LogoMark } from '@/components/site/logo'

interface AuthShellProps {
    title: ReactNode
    text?: ReactNode
    children: ReactNode
    footer?: ReactNode
}

export const AuthShell: FC<AuthShellProps> = ({ title, text, children, footer }): ReactNode => {
    const { t } = useI18n()

    return (
        <section id='auth' className='bg-sand px-4 py-10 sm:px-6 md:py-16 lg:px-8'>
            <div className='mx-auto grid max-w-6xl overflow-hidden rounded-[2rem] border border-border bg-card shadow-[0_30px_80px_-40px_rgba(19,36,58,0.45)] lg:grid-cols-[0.9fr_1.1fr]'>
                <div className='relative hidden min-h-[560px] lg:block'>
                    <Image src='/images/terrace-evening.jpg' alt='' fill sizes='(min-width: 1024px) 45vw, 0px' className='object-cover' />
                    <div aria-hidden='true' className='absolute inset-0 bg-gradient-to-t from-navy/90 via-navy/40 to-navy/10' />
                    <div className='absolute inset-x-0 bottom-0 p-10 text-white'>
                        <LogoMark tone='light' size='lg' className='size-24' />
                        <p className='mt-6 font-heading text-4xl leading-tight font-semibold'>{t.auth.sideTitle}</p>
                        <p className='mt-3 max-w-sm text-sm text-white/75'>{t.auth.sideText}</p>
                    </div>
                </div>

                <div className='flex flex-col justify-center px-6 py-10 sm:px-12 md:py-14'>
                    <h1 className='text-3xl leading-tight font-semibold text-navy md:text-4xl'>{title}</h1>
                    {text && <p className='mt-3 text-muted-foreground'>{text}</p>}
                    <div className='mt-8'>{children}</div>
                    {footer && <div className='mt-8 border-t border-border pt-6 text-sm text-muted-foreground'>{footer}</div>}
                </div>
            </div>
        </section>
    )
}

export const safeNext = (value: unknown, fallback = '/account'): string => {
    if (typeof value !== 'string') return fallback
    if (!value.startsWith('/') || value.startsWith('//') || value.startsWith('/\\')) return fallback

    return value
}
